import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LeadUiModel } from '../../../shared/ui-models/lead.ui-model';
import { LeadConfigModel, LeadSearchQueryModel } from '../../../shared/models/lead.model';
import { getLeadConfig, searchLeads } from '../../../shared/services/data/lead.data';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { SlaBadge } from '../../shared/sla-badge.component';

const PAGE_SIZE = 25;

const titleCase = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const LeadsListContainer: React.FC = () => {
  const history = useHistory();
  const [leads, setLeads] = useState<LeadUiModel[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [query, setQuery] = useState<LeadSearchQueryModel>({});

  useEffect(() => {
    getLeadConfig().then((result) => {
      if (!isErrorInfo(result)) setConfig(result);
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    return searchLeads(query, PAGE_SIZE, page * PAGE_SIZE).then((result) => {
      if (!isErrorInfo(result)) {
        setLeads(result.data);
        setTotal(result.total);
      }
      setLoading(false);
    });
  }, [query, page]);

  useEffect(() => {
    load();
  }, [load]);

  /** Setting a filter returns to page 1 — page 4 of the old result set is meaningless. */
  const setFilter = (patch: Partial<LeadSearchQueryModel>) => {
    setPage(0);
    setQuery({ ...query, ...patch });
  };

  const select = (key: keyof LeadSearchQueryModel, label: string, options: string[]) => (
    <select
      key={String(key)}
      className="rounded border border-gray-300 px-2 py-1 text-sm"
      value={(query[key] as string) ?? ''}
      onChange={(event) =>
        setFilter({ [key]: event.target.value || undefined } as Partial<LeadSearchQueryModel>)
      }
    >
      <option value="">{label}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {titleCase(option)}
        </option>
      ))}
    </select>
  );

  const detailsPath = (leadId: string) =>
    (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(':leadId', leadId);

  return (
    <div className="flex h-full flex-col items-stretch md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Leads</h1>
        <span className="text-sm text-gray-500">{total} total</span>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {config ? select('vertical', 'All verticals', config.verticals) : null}
        {config ? select('status', 'All statuses', config.statuses) : null}
        {config ? select('valueCategory', 'All values', config.valueCategories) : null}
        <input
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          placeholder="Phone"
          value={query.phone ?? ''}
          onChange={(event) => setFilter({ phone: event.target.value || undefined })}
        />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={Boolean(query.slaBreachedOnly)}
            onChange={(event) => setFilter({ slaBreachedOnly: event.target.checked || undefined })}
          />
          Breached only
        </label>
      </div>

      <div className="overflow-x-auto rounded bg-white p-4 shadow-sm">
        {loading ? (
          <Loader />
        ) : leads.length === 0 ? (
          <p className="py-8 text-center text-gray-500">No leads match these filters.</p>
        ) : (
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="text-xs uppercase text-gray-500">
              <tr>
                <th className="py-2">#</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Vertical</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>SLA</th>
                <th>Attempts</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                  onClick={() => history.push(detailsPath(lead.id))}
                >
                  <td className="py-2 text-gray-400">{lead.shortId}</td>
                  <td className="font-medium">{lead.name || '—'}</td>
                  <td>{lead.phone || lead.email || '—'}</td>
                  <td>{titleCase(lead.vertical)}</td>
                  <td>{titleCase(lead.status)}</td>
                  <td>
                    {lead.assigneeName ?? (
                      <span className="text-amber-700" title="Nobody holds this lead">
                        unassigned
                      </span>
                    )}
                  </td>
                  <td>
                    <SlaBadge state={lead.slaState} />
                  </td>
                  <td>
                    {/* Remaining, not used: "1 left" is the number an agent
                        acts on, and the quota moves when a lead is re-scored. */}
                    {lead.remainingContactAttempts} / {lead.maxContactAttempts}
                  </td>
                  <td className="text-gray-500">
                    {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
};
